import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ActionMenuItem {
  label: ReactNode;
  /** Phosphor icon class, e.g. "ph-pencil". */
  icon?: string;
  onClick: () => void;
  /** Render in the danger colour (e.g. Delete). */
  danger?: boolean;
  /** Render a divider above this item. */
  divider?: boolean;
  disabled?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Toggle content; defaults to a vertical kebab icon. */
  toggle?: ReactNode;
  /** Class for the toggle button. Defaults to an icon button. */
  toggleClassName?: string;
  /** Align the menu to the right edge of the toggle (default true). */
  alignEnd?: boolean;
  ariaLabel?: string;
  className?: string;
}

/**
 * A kebab/overflow action menu with open/close controlled in React (no Bootstrap JS
 * dependency). The menu is portaled to <body> with fixed positioning so it is never clipped
 * by a card or a `.table-responsive`/overflow ancestor. Closes on outside click or Escape.
 */
export function ActionMenu({
  items,
  toggle,
  toggleClassName = 'btn btn-light btn-icon btn-sm',
  alignEnd = true,
  ariaLabel = 'Actions',
  className = '',
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const reposition = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      top: r.bottom + 2,
      ...(alignEnd ? { right: Math.max(8, window.innerWidth - r.right) } : { left: r.left }),
    });
  };

  // Measure as soon as the menu opens.
  useLayoutEffect(() => {
    if (open) reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => reposition();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    // capture=true so we also catch scrolls inside the table's overflow container.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={`dropdown ${className}`.trim()} style={{ position: 'relative' }}>
      <button
        type="button"
        ref={btnRef}
        className={toggleClassName}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {toggle ?? <i className="ph-dots-three-vertical" />}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            className={`dropdown-menu ${alignEnd ? 'dropdown-menu-end' : ''} show`.trim()}
            style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right, zIndex: 1055 }}
          >
            {items.map((item, i) => (
              <div key={i}>
                {item.divider && <div className="dropdown-divider" />}
                <button
                  type="button"
                  className={`dropdown-item ${item.danger ? 'text-danger' : ''}`.trim()}
                  disabled={item.disabled}
                  onClick={() => {
                    item.onClick();
                    setOpen(false);
                  }}
                >
                  {item.icon && <i className={`${item.icon} me-2`} />}
                  {item.label}
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
